/**
 * クロップ計算ロジック
 *
 * iPhone カメラの撮影画像 (4:3) を、選択された中判フォーマット
 * (645/66/67/68/69/617) に「内接（fit）」でクロップする。
 *
 * クロップ枠は中央配置を基本とし、後で MAPPING 画面で位置調整可能。
 */

import { getAspectRatio } from './presets.js';

/**
 * @typedef {Object} CropBox
 * @property {number} x - 元画像の左上x（px、整数）
 * @property {number} y - 元画像の左上y（px、整数）
 * @property {number} w - クロップ幅（px、整数）
 * @property {number} h - クロップ高さ（px、整数）
 */

/**
 * 元画像の寸法と、目標アスペクト比から、中央クロップの座標を計算
 *
 * @param {number} imgW
 * @param {number} imgH
 * @param {[number, number]} aspect - [w, h] 比率（オリエンテーション適用済み）
 * @returns {CropBox}
 */
export function calculateCenterCrop(imgW, imgH, aspect) {
  const targetRatio = aspect[0] / aspect[1];
  const imgRatio = imgW / imgH;

  if (imgRatio > targetRatio) {
    // 画像が横長すぎる → 左右をクロップ
    const cropW = Math.round(imgH * targetRatio);
    return {
      x: Math.round((imgW - cropW) / 2),
      y: 0,
      w: cropW,
      h: imgH,
    };
  } else {
    // 画像が縦長すぎる → 上下をクロップ
    const cropH = Math.round(imgW / targetRatio);
    return {
      x: 0,
      y: Math.round((imgH - cropH) / 2),
      w: imgW,
      h: cropH,
    };
  }
}

/**
 * プリセットからクロップを計算（指定画像サイズに対して）
 */
export function calculateCropFromPreset(imgW, imgH, preset, orientation) {
  const aspect = getAspectRatio(preset, orientation);
  return calculateCenterCrop(imgW, imgH, aspect);
}

/**
 * クロップボックスを正規化座標 (0-1) に変換
 */
export function cropBoxToNormalized(cropBox, imgW, imgH) {
  return {
    x: cropBox.x / imgW,
    y: cropBox.y / imgH,
    w: cropBox.w / imgW,
    h: cropBox.h / imgH,
  };
}

/**
 * 正規化座標のクロップを実画像座標に戻す
 */
export function cropBoxFromNormalized(normalized, imgW, imgH) {
  return {
    x: Math.round(normalized.x * imgW),
    y: Math.round(normalized.y * imgH),
    w: Math.round(normalized.w * imgW),
    h: Math.round(normalized.h * imgH),
  };
}

/**
 * Canvas に画像とクロップボックスを描画して、新しい Blob を生成
 *
 * @param {HTMLImageElement | HTMLVideoElement | ImageBitmap} source
 * @param {CropBox} cropBox
 * @param {number} sourceW - source の実幅
 * @param {number} sourceH - source の実高
 * @param {number} [maxOutputDim=2400] - 出力の最大辺
 * @param {number} [quality=0.86] - JPEG 品質
 * @returns {Promise<Blob>}
 */
export async function cropToBlob(source, cropBox, sourceW, sourceH, maxOutputDim = 2400, quality = 0.86) {
  // 出力サイズを決定（アスペクト比維持で max 辺を maxOutputDim に）
  let outW = cropBox.w;
  let outH = cropBox.h;
  const maxSide = Math.max(outW, outH);
  if (maxSide > maxOutputDim) {
    const scale = maxOutputDim / maxSide;
    outW = Math.round(outW * scale);
    outH = Math.round(outH * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    source,
    cropBox.x, cropBox.y, cropBox.w, cropBox.h,
    0, 0, outW, outH
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

/**
 * Video 要素から現在のフレームを ImageBitmap として取得
 */
export async function captureFrameFromVideo(video) {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) throw new Error('video not ready');
  // Safari の ImageBitmap サポート確認
  if ('createImageBitmap' in window) {
    return await createImageBitmap(video);
  }
  // フォールバック: canvas 経由
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(video, 0, 0, w, h);
  return canvas;
}

/**
 * Blob → HTMLImageElement
 */
export function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
